import {Text} from '../elements/Text';
import clsx from 'clsx';
import {Button, PageSectionContainer} from '~/components';
import Tabs from '~/components/elements/Tabs.client';
// @ts-ignore
import {Tab} from '@headlessui/react';
import {
  CardSecondaryCategoryEntity,
  TabbedThreeCardsSecondaryEntity,
} from '~/types';
import {SpreadMedia} from '../media/SpreadMedia';
import {DATA_TEST_IDS} from '~/lib/const';

export function TabbedThreeCardsSecondary({
  data,
}: {
  data: TabbedThreeCardsSecondaryEntity;
}) {
  const tabEnd = data.seeAllLink ? (
    <Button
      variant="primary-inverse"
      size="md"
      className="py-[11px] hidden md:block"
      to={data.seeAllLink}
    >
      SEE ALL
    </Button>
  ) : null;

  return (
    <PageSectionContainer testid={DATA_TEST_IDS.PRODUCT_TAB_CAROUSEL}>
      <div className="flex flex-col max-w-full w-[748px] pr-5">
        <div className={'flex justify-start align-center'}>
          {data.title && (
            <Text
              testid={DATA_TEST_IDS.TABBED_THREE_CARDS_SECONDARY_TITLE}
              as="h3"
            >
              {data.title.toUpperCase()}
            </Text>
          )}
        </div>
        {data.description && (
          <div className={clsx(data.title && 'mt-2.5 xl:mt-5')}>
            <Text
              as="p"
              testid={DATA_TEST_IDS.TABBED_THREE_CARDS_SECONDARY_DESCRIPTION}
              className={'text-sm leading-[19px]'}
            >
              {data.description}
            </Text>
          </div>
        )}
      </div>
      {data.categoryArray && (
        <Tabs
          // `md` because of arrow buttons are hidden below md
          className={'mt-[17px]'}
          titles={data.categoryArray.map((i) => i.title)}
          tabEnd={tabEnd}
          hideHeader={data.categoryArray.length <= 1}
        >
          {data.categoryArray?.map((category) => (
            <Tab.Panel key={category.name} className={'mt-[31px] lg:mt-12'}>
              {category.cardArray && (
                <div
                  data-testid={DATA_TEST_IDS.TAB}
                  className="flex flex-col space-y-5 lg:space-y-0 lg:flex-row lg:space-x-[38px]"
                >
                  <div className="w-full flex-1">
                    {category.cardArray[0] && (
                      <CardSecondary card={category.cardArray[0]} size="lg" />
                    )}
                  </div>

                  <div className="space-y-5 lg:space-y-[38px] w-full flex-1">
                    {category.cardArray[1] && (
                      <CardSecondary card={category.cardArray[1]} size="md" />
                    )}

                    {category.cardArray[2] && (
                      <CardSecondary card={category.cardArray[2]} size="md" />
                    )}
                  </div>
                </div>
              )}
            </Tab.Panel>
          ))}
        </Tabs>
      )}

      {data.seeAllLink && (
        <div className="flex justify-center">
          <Button
            data-testid={DATA_TEST_IDS.BUTTON('see-all')}
            variant="secondary"
            size="md"
            className="md:hidden mt-[30px]"
            to={data.seeAllLink}
          >
            SEE ALL
          </Button>
        </div>
      )}
    </PageSectionContainer>
  );
}

function CardSecondary({
  card,
  size,
}: {
  card: CardSecondaryCategoryEntity;
  size: 'lg' | 'md';
}) {
  return (
    <div
      data-testid={DATA_TEST_IDS.CARD_SECONDARY}
      className={clsx('relative w-full', {
        'h-[195px] lg:h-[379px]': size === 'md',
        'aspect-square lg:aspect-auto lg:h-[796px]': size === 'lg',
      })}
    >
      {card.image && (
        <SpreadMedia
          media={{
            assetType: 'photo',
            photo: {
              heroImage: card.image,
            },
          }}
          breakpoints={[
            [230, 230],
            [434, 434],
          ]}
        />
      )}

      <div className="flex flex-col items-start absolute left-[41px] lg:left-[50px] right-0 bottom-[37px] lg:bottom-[50px] z-[1px]">
        {card.title && (
          <div
            data-testid={DATA_TEST_IDS.CARD_TITLE}
            className={clsx('mt-5 max-w-[748px] p-2 rounded-lg', {
              'backdrop-blur-md': (card.backgroundOpacity ?? 0) > 0,
            })}
            style={{
              backgroundColor:
                card?.backgroundColor === 'white'
                  ? `rgba(255,255,255,${(card.backgroundOpacity ?? 0) / 100})`
                  : card?.backgroundColor === 'black'
                  ? `rgba(0,0,0,${(card.backgroundOpacity ?? 0) / 100})`
                  : 'transparent',
            }}
          >
            {card.title && (
              <Text as="h2" className="md:truncate line-clamp-2">
                {card.title.toUpperCase()}
              </Text>
            )}
          </div>
        )}
        <Button
          data-testid={DATA_TEST_IDS.BUTTON('card-button')}
          maxWidth={250}
          to={card.button?.buttonHref}
          key={card.button?.buttonText}
          variant={card.button?.buttonType}
          className="mt-5 lg:mt-[25px] md:mt-9 truncate"
        >
          {card.button?.buttonText?.toUpperCase()}
        </Button>
      </div>
    </div>
  );
}
